import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ReactNode } from 'react';

interface ErrorState {
  isVisible?: boolean;
  title?: string;
  message?: string;
  ctas?: { label: string; type: string; primary?: boolean; payload?: Record<string, any> }[];
  showErrorToast?: boolean;
  toastMessage?: string;
  /** Increments on every showErrorToast dispatch so consumers can detect a re-show even when the message text is unchanged (e.g. repeated identical validation errors). */
  toastId?: number;
  successToast?: boolean;
  isNetworkError?: boolean;
  screen?: string;
  subTitle?: string;
  noTranslation?: boolean;
  /** When true, the `message` field is rendered with textDecorationLine: 'underline'. */
  underlineMessage?: boolean;
  /** Set to true when the user confirms "Yes" on an overtime dialog */
  overtimeConfirmed?: boolean;
  /** Set to true when the user clicks "No" on a technician-overtime dialog
   *  (repair should still proceed but with technicianDeclinedOvertime = true) */
  overtimeDeclined?: boolean;
  /**
   * Controls where the toast is rendered.
   * true  (default) → rendered globally in App.tsx
   * false           → rendered inline within the calling page/component
   */
  parentView?: boolean;
  /** Optional icon node to display inside the modal. */
  icon?: ReactNode;
  /** Set to true when any API returns the "session expired" message string.
   *  Triggers the SessionExpiredModal in MainLayoutScreen. */
  sessionExpiredMessage?: boolean;
}

const initialState: ErrorState = {
  isVisible: false,
  title: '',
  subTitle: '',
  message: '',
  ctas: [],
  showErrorToast: false,
  toastMessage: '',
  toastId: 0,
  successToast: false,
  isNetworkError: false,
  screen: '',
  noTranslation: false,
  overtimeConfirmed: false,
  overtimeDeclined: false,
  parentView: true,
  icon: null,
  sessionExpiredMessage: false,
};

const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    showErrorModal(
      state,
      action: PayloadAction<{
        title: string;
        subTitle: string;
        message: string;
        ctas: { label: string; type: string; primary?: boolean; payload?: Record<string, any> }[];
        screen: string;
        noTranslation: boolean;
        icon?: ReactNode;
        /** When true, the `message` field is rendered with textDecorationLine: 'underline'. */
        underlineMessage?: boolean;
      }>,
    ) {
      state.isVisible = true;
      state.title = action.payload.title;
      state.subTitle = action.payload.subTitle;
      state.message = action.payload.message;
      state.ctas = action.payload.ctas;
      state.screen = action.payload.screen;
      state.noTranslation = action.payload.noTranslation;
      state.icon = action.payload.icon ?? null;
      state.underlineMessage = action.payload.underlineMessage ?? false;
    },
    hideErrorModal(state) {
      state.isVisible = false;
      state.title = '';
      state.subTitle = '';
      state.message = '';
      state.ctas = [];
      state.screen = '';
      state.noTranslation = false;
      state.icon = null;
      state.underlineMessage = false;
    },
    /** Called when the user presses "Yes" on a technician/labour overtime modal */
    confirmOvertimeYes(state) {
      state.overtimeConfirmed = true;
    },
    /** Reset the overtime confirmation flag after it has been consumed */
    resetOvertimeConfirm(state) {
      state.overtimeConfirmed = false;
    },
    /** Called when the user presses "No" on a technician-overtime modal —
     *  the repair still proceeds but with technicianDeclinedOvertime = true */
    confirmOvertimeNo(state) {
      state.overtimeDeclined = true;
    },
    /** Reset the overtime-declined flag after it has been consumed */
    resetOvertimeDecline(state) {
      state.overtimeDeclined = false;
    },
    showErrorToast(
      state,
      action: PayloadAction<{
        toastMessage: string;
        successToast?: boolean;
        /** false → render toast inline in the calling page; true (default) → render globally in App.tsx */
        parentView?: boolean;
      }>,
    ) {
      // Do not show the error toast while the session-expired modal is active —
      // the modal itself communicates the error and the two must not overlap.
      if (state.sessionExpiredMessage) return;
      state.showErrorToast = true;
      state.toastMessage = action.payload.toastMessage;
      state.toastId = (state.toastId ?? 0) + 1;
      state.successToast = action.payload.successToast;
      state.parentView = action.payload.parentView ?? true;
    },
    hideErrorToast(state) {
      state.showErrorToast = false;
      state.toastMessage = '';
      state.successToast = false;
    },
    setIsNetworkError(
      state,
      action: PayloadAction<{
        isNetworkError: boolean;
      }>,
    ) {
      state.isNetworkError = action.payload.isNetworkError;
    },
    /** Show the session-expired message modal (triggered by any API returning the
     *  "Your session has expired, Please sign in again to continue" response message).
     *  Also suppresses any currently-visible error toast so the two don't overlap. */
    showSessionExpiredMessage(state) {
      state.sessionExpiredMessage = true;
      state.showErrorToast = false;
      state.toastMessage = '';
      state.successToast = false;
    },
    /** Hide the session-expired message modal after the user acknowledges it. */
    hideSessionExpiredMessage(state) {
      state.sessionExpiredMessage = false;
    },
  },
});

export const {
  showErrorModal,
  hideErrorModal,
  showErrorToast,
  hideErrorToast,
  setIsNetworkError,
  confirmOvertimeYes,
  resetOvertimeConfirm,
  confirmOvertimeNo,
  resetOvertimeDecline,
  showSessionExpiredMessage,
  hideSessionExpiredMessage,
} = errorSlice.actions;
export default errorSlice.reducer;
